import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';

// ===== Optional Google Analytics (replace GA_MEASUREMENT_ID with your ID) =====
const GA_MEASUREMENT_ID = 'G-XXXXXXX'; // <-- replace with your GA4 Measurement ID or leave as placeholder
if (typeof window !== 'undefined' && GA_MEASUREMENT_ID && !window.gtagInitialized) {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag; // expose globally so other code can call window.gtag
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID);
  window.gtagInitialized = true;
}

// Track outbound link clicks (safe: falls back to dataLayer or console)
const trackClick = (label) => {
  if (typeof window !== 'undefined') {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'outbound_click', event_category: 'outbound', event_label: label });
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'click', { event_category: 'outbound', event_label: label });
    }
    // Fallback for local dev
    console.log('Tracked click:', label);
  }
};

// Translations object for multi-language support
const translations = {
  'vi': {
    appTitle: 'AI đoán Giá Coin',
    selectLanguageLabel: 'Chọn Ngôn ngữ',
    selectCoinLabel: 'Chọn Đồng Coin:',
    searchCoinPlaceholder: 'Tìm kiếm đồng coin...',
    predictButtonText: 'Phân tích & Dự đoán',
    stopButtonText: 'Dừng Phân tích',
    loadingButtonText: 'Đang phân tích...',
    loadingMessage: 'Đang tải...',
    loadingData: 'Đang tải dữ liệu lịch sử...',
    fetchingCoinList: 'Đang tải danh sách đồng coin...',
    readyForPrediction: 'Sẵn sàng để dự đoán.',
    dataLoadingError: 'Không thể tải dữ liệu lịch sử. Vui lòng thử lại sau.',
    coinListLoadingError: 'Không thể tải danh sách đồng coin. Vui lòng thử lại sau.',
    noDataForPrediction: 'Không có dữ liệu lịch sử để phân tích. Vui lòng tải lại.',
    predictionTitle: 'Kết quả Dự đoán:',
    predictionConnectingBackend: 'Đang gửi dữ liệu đến backend...',
    predictionSuccess: (coinId, price) => `Giá dự đoán cho ${coinId.toUpperCase()} vào ngày tiếp theo: ${price}`,
    predictionError: (message) => `Lỗi khi dự đoán: ${message}. Có vấn đề kết nối server.`,
    predictionStopped: 'Đã dừng quá trình phân tích.',
    chartTitle: 'Biểu đồ Giá & Vốn hóa thị trường & Khối lượng giao dịch:',
    tableTitle: 'Dữ liệu Giá Lịch sử (Chi tiết):',
    tableDateHeader: 'Ngày',
    tablePriceHeader: 'Giá (USD)',
    predictionLabel: '(Dự đoán)',
    priceLabel: 'Giá',
    dateLabel: 'Ngày',
    actualLabel: 'Thực tế',
    noPrediction: 'Chưa có dự đoán nào.',
    analysisSteps: {
      preparing: 'Đang chuẩn bị dữ liệu...',
      training: 'Đang huấn luyện mô hình AI...',
      predicting: 'Đang thực hiện dự đoán...',
      complete: 'Phân tích hoàn tất.',
    },
    languageName: 'Tiếng Việt',
    disclaimerTitle: 'Tuyên bố miễn trừ trách nhiệm',
    disclaimerContent: (
      <>
        Đây là một ứng dụng dự đoán giá tiền điện tử được xây dựng dựa trên mô hình học máy. Thông tin và dự đoán được cung cấp chỉ nhằm mục đích tham khảo và giáo dục, không phải là lời khuyên tài chính hay khuyến nghị đầu tư.
        <br /><br />
        Các dự đoán được tạo ra dựa trên dữ liệu lịch sử và các thuật toán phức tạp. Tuy nhiên, thị trường tiền điện tử cực kỳ biến động và khó lường. Giá trị có thể thay đổi nhanh chóng và bất ngờ, do đó, không có gì đảm bảo rằng các dự đoán sẽ chính xác.
        <br /><br />
        Bạn nên tự chịu trách nhiệm hoàn toàn cho mọi quyết định đầu tư. Luôn tìm kiếm lời khuyên từ các chuyên gia tài chính có trình độ trước khi đưa ra bất kỳ quyết định nào liên quan đến đầu tư.
      </>
    ),
    footer1: "Copyright © 2025 AI đoán Giá Coin -",
    footer2: "coinvision.aidream@gmail.com",
    marketCapLabel: 'Vốn hóa thị trường',
    volumeLabel: 'Khối lượng giao dịch',
    donateTitle: 'Hỗ trợ & Giao dịch',
    donateDesc: 'Ủng hộ phát triển hoặc mở tài khoản sàn qua link referral của chúng tôi.',
    donateSupportBtn: 'Ủng hộ',
    okxBtn: 'Mở tài khoản OKX (Nhận tới 100 USDT khi bạn đăng ký OKX bằng liên kết giới thiệu của tôi tại đây!)',
    binanceBtn: 'Mở tài khoản Binance (NHỮNG TOKEN TUYỆT VỜI TRỊ GIÁ 450.000 USD ĐANG CHỜ ĐÓN BẠN!)',
    donatePoppTitle: 'Ủng hộ để phát triển ứng dụng',
    donatePoppDesc: 'Ứng dụng miễn phí nhưng cần tài trợ để duy trì server & phát triển thêm tính năng.',
    donatePoppBtnBMC: 'Donate qua BuyMeACoffee',
    donatePoppBtnCrypto: 'Donate bằng Crypto',
    donatePoppQR: 'Quét QR hoặc copy địa chỉ ví USDT (TRC20):',
  },
  'en': {
    appTitle: 'Coin Price Prediction AI',
    selectLanguageLabel: 'Select Language',
    selectCoinLabel: 'Select Coin:',
    searchCoinPlaceholder: 'Search for a coin...',
    predictButtonText: 'Analyze & Predict',
    stopButtonText: 'Stop Analysis',
    loadingButtonText: 'Analyzing...',
    loadingMessage: 'Loading...',
    loadingData: 'Fetching historical data...',
    fetchingCoinList: 'Fetching coin list...',
    readyForPrediction: 'Ready for prediction.',
    dataLoadingError: 'Could not load historical data. Please try again later.',
    coinListLoadingError: 'Could not load the coin list. Please try again later.',
    noDataForPrediction: 'No historical data to analyze. Please reload.',
    predictionTitle: 'Prediction Result:',
    predictionConnectingBackend: 'Sending data to backend...',
    predictionSuccess: (coinId, price) => `The predicted price for ${coinId.toUpperCase()} for the next day: ${price}`,
    predictionError: (message) => `Prediction error: ${message}. Please ensure the backend is running.`,
    predictionStopped: 'Analysis has been stopped.',
    chartTitle: 'Price, Market Cap & Volume Chart:',
    tableTitle: 'Historical Price Data (Details):',
    tableDateHeader: 'Date',
    tablePriceHeader: 'Price (USD)',
    predictionLabel: '(Prediction)',
    priceLabel: 'Price',
    dateLabel: 'Date',
    actualLabel: 'Actual',
    noPrediction: 'No prediction has been made yet.',
    analysisSteps: {
      preparing: 'Preparing data...',
      training: 'Training the AI model...',
      predicting: 'Making the prediction...',
      complete: 'Analysis complete.',
    },
    languageName: 'English',
    disclaimerTitle: 'Disclaimer',
    disclaimerContent: (
      <>
        This is a cryptocurrency price prediction application built on a machine learning model. The information and predictions provided are for reference and educational purposes only, and do not constitute financial advice or investment recommendations.
        <br /><br />
        The predictions are generated based on historical data and complex algorithms. However, the cryptocurrency market is extremely volatile and unpredictable. Prices can change rapidly and unexpectedly; therefore, there is no guarantee that the predictions will be accurate.
        <br /><br />
        You should take full responsibility for any investment decisions. Always seek advice from qualified financial professionals before making any investment-related decisions.
      </>
    ),
    footer1: 'Copyright © 2025 AI Coin Price Prediction -',
    footer2: "coinvision.aidream@gmail.com",
    marketCapLabel: 'Market Cap',
    volumeLabel: 'Total Volume',
    donateTitle: 'Support & Transactions',
    donateDesc: 'Support our development or open an exchange account through our referral link.',
    donateSupportBtn: 'Support',
    okxBtn: 'Open an OKX account (Get up to 100 USDT when you sign up for OKX using my referral link here!)',
    binanceBtn: 'Open a Binance account (Amazing tokens worth $450,000 are waiting for you!)',
    donatePoppTitle: 'Support to develop the app',
    donatePoppDesc: 'The app is free, but we need funding to maintain the server and develop more features.',
    donatePoppBtnBMC: 'Donate via BuyMeACoffee',
    donatePoppBtnCrypto: 'Support with Crypto',
    donatePoppQR: 'Scan the QR code or copy the USDT (TRC20) wallet address:',
  },
  'zh-CN': {
    appTitle: '加密货币价格预测AI',
    selectLanguageLabel: '选择语言',
    selectCoinLabel: '选择加密货币:',
    searchCoinPlaceholder: '搜索加密货币...',
    predictButtonText: '分析和预测',
    stopButtonText: '停止分析',
    loadingButtonText: '正在分析...',
    loadingMessage: '加载中...',
    loadingData: '正在获取历史数据...',
    fetchingCoinList: '正在获取加密货币列表...',
    readyForPrediction: '准备好进行预测。',
    dataLoadingError: '无法加载历史数据。请稍后再试。',
    coinListLoadingError: '无法加载加密货币列表。请稍后再试。',
    noDataForPrediction: '没有可供分析的历史数据。请重新加载。',
    predictionTitle: '预测结果:',
    predictionConnectingBackend: '正在向后端发送数据...',
    predictionSuccess: (coinId, price) => `下一个交易日 ${coinId.toUpperCase()} 的预测价格：${price}`,
    predictionError: (message) => `预测错误: ${message}。请确保后端正在运行。`,
    predictionStopped: '分析已停止。',
    chartTitle: '价格、市值和交易量图表:',
    tableTitle: '历史价格数据 (详情):',
    tableDateHeader: '日期',
    tablePriceHeader: '价格 (美元)',
    predictionLabel: '(预测)',
    priceLabel: '价格',
    dateLabel: '日期',
    actualLabel: '实际',
    noPrediction: '尚未进行任何预测。',
    analysisSteps: {
      preparing: '正在准备数据...',
      training: '正在训练 AI 模型...',
      predicting: '正在进行预测...',
      complete: '分析完成。',
    },
    languageName: '中文',
    disclaimerTitle: '免责声明',
    disclaimerContent: (
      <>
        本加密货币价格预测模型是基于机器学习模型构建的。提供的信息和预测仅供参考和教育目的，不构成财务建议或投资推荐。
        <br /><br />
        预测是根据历史数据和复杂算法生成的。然而，加密货币市场波动性大且不可预测。价格可能迅速意外地变化，因此不保证预测的准确性。
        <br /><br />
        您应对所有投资决策承担全部责任。在做出任何投资决策之前，请务必寻求合格财务专业人士的建议。
      </>
    ),
    footer1: 'Copyright © 2025 AI加密货币价格预测 -',
    footer2: "coinvision.aidream@gmail.com",
    marketCapLabel: '市值',
    volumeLabel: '交易量',
    donateTitle: '支持与交易',
    donateDesc: '请通过我们的推荐链接开设交易所账户，以支持我们的发展。',
    donateSupportBtn: '支持',
    okxBtn: '开设OKX账户（通过我的推荐链接注册可获得高达100 USDT！）',
    binanceBtn: '开设Binance账户（价值45万美元的精彩代币正等着你！）',
    donatePoppTitle: '支持应用开发',
    donatePoppDesc: '本应用是免费的，但需要资助来维持服务器并开发更多功能。',
    donatePoppBtnBMC: '通过 BuyMeACoffee 捐赠',
    donatePoppBtnCrypto: '用加密货币支持我',
    donatePoppQR: '扫描二维码或复制USDT (TRC20) 钱包地址：',
  },
  'ja-JP': {
    appTitle: '仮想通貨価格予測AI',
    selectLanguageLabel: '言語を選択',
    selectCoinLabel: '仮想通貨を選択:',
    searchCoinPlaceholder: '仮想通貨を検索...',
    predictButtonText: '分析と予測',
    stopButtonText: '分析を停止',
    loadingButtonText: '分析中...',
    loadingMessage: '読み込み中...',
    loadingData: '履歴データを取得中...',
    fetchingCoinList: '仮想通貨リストを取得中...',
    readyForPrediction: '予測の準備ができました。',
    dataLoadingError: '履歴データを読み込めませんでした。後でもう一度お試しください。',
    coinListLoadingError: '仮想通貨リストを読み込めませんでした。後でもう一度お試しください。',
    noDataForPrediction: '分析する履歴データがありません。再読み込みしてください。',
    predictionTitle: '予測結果:',
    predictionConnectingBackend: 'バックエンドにデータを送信中...',
    predictionSuccess: (coinId, price) => `翌日の ${coinId.toUpperCase()} の予測価格: ${price}`,
    predictionError: (message) => `予測エラー: ${message}。バックエンドが実行されていることを確認してください。`,
    predictionStopped: '分析が停止されました。',
    chartTitle: '価格、時価総額、取引量チャート:',
    tableTitle: '履歴価格データ (詳細):',
    tableDateHeader: '日付',
    tablePriceHeader: '価格 (USD)',
    predictionLabel: '(予測)',
    priceLabel: '価格',
    dateLabel: '日付',
    actualLabel: '実際',
    noPrediction: 'まだ予測が行われていません。',
    analysisSteps: {
      preparing: 'データを準備中...',
      training: 'AIモデルをトレーニング中...',
      predicting: '予測を実行中...',
      complete: '分析完了。',
    },
    languageName: '日本語',
    disclaimerTitle: '免責事項',
    disclaimerContent: (
      <>
        これは機械学習モデルに基づいて構築された暗号通貨価格予測アプリケーションです。提供される情報や予測は、あくまで参考および教育目的のみであり、金融アドバイスや投資推奨を意味するものではありません。
        <br /><br />
        予測は過去のデータと複雑なアルゴリズムに基づいて生成されます。しかし、暗号通貨市場は非常に変動が激しく予測困難です。価格は急速かつ予期せぬ変動をする可能性があり、予測の正確性は保証されません。
        <br /><br />
        投資判断はすべて自己責任で行ってください。投資に関するいかなる決定を下す前にも、必ず資格を有する金融専門家に相談してください。
      </>
    ),
    footer1: 'Copyright © 2025 AI仮想通貨価格予測 -',
    footer2: "coinvision.aidream@gmail.com",
    marketCapLabel: '時価総額',
    volumeLabel: '取引量',
    donateTitle: 'サポート＆取引',
    donateDesc: '私たちのリファーラルリンクから取引所のアカウントを開設して、開発をサポートしてください。',
    donateSupportBtn: '支持',
    okxBtn: 'OKXアカウントを開設する（こちらの紹介リンクから登録すると最大100USDTを獲得できます！）',
    binanceBtn: 'Binanceアカウントを開設する（45万ドル相当の素晴らしいトークンがあなたを待っています！）',
    donatePoppTitle: 'アプリ開発を支援する',
    donatePoppDesc: 'このアプリは無料ですが、サーバーの維持や新機能の開発のためにご支援が必要です。',
    donatePoppBtnBMC: 'BuyMeACoffeeで寄付する',
    donatePoppBtnCrypto: '暗号資産で応援する',
    donatePoppQR: 'QRコードをスキャンするか、USDT（TRC20）ウォレットアドレスをコピーしてください：',
  },
  'ko-KR': {
    appTitle: '코인 가격 예측 AI',
    selectLanguageLabel: '언어 선택',
    selectCoinLabel: '코인 선택:',
    searchCoinPlaceholder: '코인 검색...',
    predictButtonText: '분석 및 예측',
    stopButtonText: '분석 중지',
    loadingButtonText: '분석 중...',
    loadingMessage: '로딩 중...',
    loadingData: '과거 데이터 가져오는 중...',
    fetchingCoinList: '코인 목록 가져오는 중...',
    readyForPrediction: '예측 준비 완료.',
    dataLoadingError: '과거 데이터를 불러올 수 없습니다. 나중에 다시 시도해 주세요.',
    coinListLoadingError: '코인 목록을 불러올 수 없습니다. 나중에 다시 시도해 주세요.',
    noDataForPrediction: '분석할 과거 데이터가 없습니다. 다시 로드해 주세요.',
    predictionTitle: '예측 결과:',
    predictionConnectingBackend: '백엔드로 데이터 전송 중...',
    predictionSuccess: (coinId, price) => `다음 날 ${coinId.toUpperCase()}의 예측 가격: ${price}`,
    predictionError: (message) => `예측 오류: ${message}. 백엔드가 실행 중인지 확인하세요.`,
    predictionStopped: '분석이 중지되었습니다.',
    chartTitle: '가격, 시가총액 및 거래량 차트:',
    tableTitle: '과거 가격 데이터 (세부 정보):',
    tableDateHeader: '날짜',
    tablePriceHeader: '가격 (USD)',
    predictionLabel: '(예측)',
    priceLabel: '가격',
    dateLabel: '날짜',
    actualLabel: '실제',
    noPrediction: '아직 예측이 이루어지지 않았습니다.',
    analysisSteps: {
      preparing: '데이터 준비 중...',
      training: 'AI 모델 학습 중...',
      predicting: '예측 수행 중...',
      complete: '분석 완료.',
    },
    languageName: '한국어',
    disclaimerTitle: '면책 조항',
    disclaimerContent: (
      <>
        이 애플리케이션은 머신러닝 모델을 기반으로 한 암호화폐 가격 예측 앱입니다. 제공되는 정보와 예측은 참고 및 교육 목적일 뿐이며, 재정 조언이나 투자 권고가 아닙니다.
        <br /><br />
        예측은 과거 데이터와 복잡한 알고리즘을 기반으로 생성됩니다. 그러나 암호화폐 시장은 매우 변동성이 크고 예측하기 어렵습니다. 가격은 빠르고 예기치 않게 변동할 수 있으며, 예측의 정확성은 보장되지 않습니다.
        <br /><br />
        모든 투자 결정에 대한 책임은 본인에게 있습니다. 투자와 관련된 어떤 결정을 내리기 전에 반드시 자격을 갖춘 금융 전문가의 조언을 받으십시오.
      </>
    ),
    footer1: 'Copyright © 2025 AI 코인 가격 예측 -',
    footer2: "coinvision.aidream@gmail.com",
    marketCapLabel: '시가총액',
    volumeLabel: '거래량',
    donateTitle: '지원 및 거래',
    donateDesc: '저희 추천 링크를 통해 거래소 계정을 개설하여 발전을 지원해 주세요.',
    donateSupportBtn: '지원',
    okxBtn: 'OKX 계정 개설하기 (제 추천 링크로 가입하면 최대 100 USDT를 받을 수 있습니다!)',
    binanceBtn: 'Binance 계정 개설하기 (45만 달러 상당의 멋진 토큰이 당신을 기다리고 있습니다!)',
    donatePoppTitle: '앱 개발을 지원하다',
    donatePoppDesc: '이 앱은 무료이지만, 서버 유지와 추가 기능 개발을 위해 후원이 필요합니다.',
    donatePoppBtnBMC: 'BuyMeACoffee로 후원하기',
    donatePoppBtnCrypto: '암호화폐로 응원하기',
    donatePoppQR: '코드를 스캔하거나 USDT (TRC20) 지갑 주소를 복사하세요:',
  },
};

// SVG logo component to be reused
const Logo = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mx-auto"
  >
    <rect x="0" y="0" width="100" height="100" rx="25" fill="#4B5563" />
    <path
      d="M30 70 L45 50 L60 65 L75 35"
      stroke="#3B82F6"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="75" cy="35" r="5" fill="#3B82F6" />
    <path
      d="M50 20 C60 20 65 30 55 35 C45 40 40 30 50 20 Z"
      fill="#FCD34D"
      stroke="#F59E0B"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <text
      x="50"
      y="85"
      fontFamily="sans-serif"
      fontSize="12"
      fontWeight="bold"
      fill="#E5E7EB"
      textAnchor="middle"
    >
      AI
    </text>
  </svg>
);

// Add an object to map language codes to flag image URLs
const languageFlags = {
  'vi': 'https://flagcdn.com/w160/vn.png',
  'en': 'https://flagcdn.com/w160/us.png',
  'zh-CN': 'https://flagcdn.com/w160/cn.png',
  'ja-JP': 'https://flagcdn.com/w160/jp.png',
  'ko-KR': 'https://flagcdn.com/w160/kr.png',
};

// Earth icon component
const EarthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" viewBox="0 0 31 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </svg>
);


// Helper function to format prices, keeping more decimal places for small values
const formatPrice = (price) => {
  if (price === 0) return '$0.00';
  if (price < 1) {
    // Show 8 decimal places for values < 1 USD, removing trailing zeros
    return `$${parseFloat(price.toFixed(8))}`;
  }
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Language options for the new button group
const languageOptions = [
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'en', label: 'English' },
  { code: 'zh-CN', label: '中文 (Chinese)' },
  { code: 'ja-JP', label: '日本語 (Japanese)' },
  { code: 'ko-KR', label: '한국어 (Korean)' },
];

// ===== Donate Popup Component =====
const DonatePopup = ({ buyme = 'https://buymeacoffee.com/coinvision', qrSrc = '/qr-usdt.jpg', address = 'UQAXai3FiiJS4SzjMp_IY5dscxOmNeJXvFHOaph306YDDiKG' }) => {
  const [open, setOpen] = useState(false);
  const [showCrypto, setShowCrypto] = useState(false);

  return (
    <div className="text-center">
      <button
        onClick={() => { setOpen(true); trackClick('Open Donate Popup'); }}
        className="w-full bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-xl font-semibold shadow"
      >
        🙏 {t.donateSupportBtn}
      </button>

      {open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full relative shadow-lg">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">✖</button>
            <h2 className="text-xl font-bold mb-2">{t.donatePoppTitle} 🚀</h2>
            <p className="text-gray-600 text-sm mb-4">{t.donatePoppDesc}</p>

            <div className="space-y-3">
              <button
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-xl font-semibold"
                onClick={() => { trackClick('BuyMeACoffee'); window.open(buyme, '_blank'); }}
              >
                ☕ {t.donatePoppBtnBMC}
              </button>

              <button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-semibold"
                onClick={() => { setShowCrypto(!showCrypto); trackClick('Toggle Crypto Donate'); }}
              >
                🪙 {t.donatePoppBtnCrypto}
              </button>
            </div>

            {showCrypto && (
              <div className="mt-4 text-center">
                <p className="text-gray-700 text-sm mb-2">{t.donatePoppQR}</p>
                <img src={qrSrc} alt="Crypto QR" className="mx-auto w-64 rounded-lg border" />
                <p className="text-xs text-gray-500 mt-2 break-all">{address}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

let t = translations['vi'];

const App = () => {
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [predictionResult, setPredictionResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [apiHistoricalData, setApiHistoricalData] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [language, setLanguage] = useState('vi');
  const [controller, setController] = useState(null); // AbortController state
  const [analysisProgress, setAnalysisProgress] = useState(''); // State for progress text
  const [predictionProgress, setPredictionProgress] = useState(0); // State for progress bar percentage
  const [dropdownOpen, setDropdownOpen] = useState(false); // State for custom dropdown
  const [coinList, setCoinList] = useState([]); // State to hold the full list of coins
  const [searchTerm, setSearchTerm] = useState(''); // State for search input
  const [showDisclaimer, setShowDisclaimer] = useState(true); // State to show/hide the disclaimer
  const [showScroll, setShowScroll] = useState(false); // State for scroll to top button
  const dropdownRef = useRef(null); // Ref for the custom dropdown
  // const t = translations[language];
  t = translations[language];

  // URL for your backend server
  //const BACKEND_URL = 'http://localhost:5000';
  //const BACKEND_URL = 'https://coinvision-backend.aidream.one';
  //const BACKEND_URL = 'https://coinvision-backend-nodejsv16-20-2.aidream.one';
  const BACKEND_URL = 'https://coinvision-backend-20250820.aidream.one';

  // Helper function to format large numbers for the chart (e.g., K, M, B)
  const formatLargeNumber = (num) => {
    if (Math.abs(num) >= 1.0e+9) {
      return (Math.abs(num) / 1.0e+9).toFixed(2) + ' B';
    }
    if (Math.abs(num) >= 1.0e+6) {
      return (Math.abs(num) / 1.0e+6).toFixed(2) + ' M';
    }
    if (Math.abs(num) >= 1.0e+3) {
      return (Math.abs(num) / 1.0e+3).toFixed(2) + ' K';
    }
    return num.toFixed(2);
  };


  // Function to fetch historical data from CoinGecko (for display and as input data)
  const fetchCoinHistoricalData = async (coinId) => {
    setIsLoading(true);
    setFetchError(null);
    setPredictionResult(t.loadingData);
    setAnalysisProgress('');
    try {
      // The API returns prices, market_caps, and total_volumes
      // UPDATED: Fetch 180 days of data for scrolling
      const response = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=180`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Merge all three data sets into a single, unified format
      const formattedData = data.prices.map((priceItem, index) => {
        const date = new Date(priceItem[0]).toLocaleDateString(language === 'en' ? 'en-US' : 'vi-VN');
        const marketCap = data.market_caps[index][1];
        const totalVolume = data.total_volumes[index][1];
        return {
          date: date,
          price: priceItem[1],
          market_cap: marketCap,
          total_volume: totalVolume,
        };
      });

      setApiHistoricalData(formattedData);
      setHistoricalData(formattedData);
      setPredictionResult(t.readyForPrediction);
    } catch (error) {
      console.error("Error fetching historical data:", error);
      setFetchError(t.dataLoadingError);
      setApiHistoricalData([]);
      setHistoricalData([]);
      setPredictionResult(t.dataLoadingError);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch the full list of coins when the app starts
  useEffect(() => {
    const fetchCoinList = async () => {
      setPredictionResult(t.fetchingCoinList);
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&per_page=250&page=1');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const simplifiedList = data.map(coin => ({
          id: coin.id,
          symbol: coin.symbol,
          name: coin.name,
          icon: coin.image, // Use the image field directly from the API
        }));
        setCoinList(simplifiedList);
        if (simplifiedList.length > 0) {
          setSelectedCoin(simplifiedList[0].id);
        }
        setPredictionResult(t.readyForPrediction);
      } catch (error) {
        console.error("Error fetching coin list:", error);
        setFetchError(t.coinListLoadingError);
        setPredictionResult(t.coinListLoadingError);
      }
    };
    fetchCoinList();
  }, []);

  // Effect to fetch historical data whenever the selected coin changes
  useEffect(() => {
    if (selectedCoin) {
      fetchCoinHistoricalData(selectedCoin);
      setShowDisclaimer(true); // Re-show the disclaimer when the coin changes
    }
  }, [selectedCoin, language]);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  // Effect to handle showing/hiding the scroll button
  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScroll && window.pageYOffset > 400) {
        setShowScroll(true);
      } else if (showScroll && window.pageYOffset <= 400) {
        setShowScroll(false);
      }
    };
    window.addEventListener('scroll', checkScrollTop);
    return () => window.removeEventListener('scroll', checkScrollTop);
  }, [showScroll]);

  // Function to scroll to the top of the page
  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to call the backend for LSTM prediction
  const getLSTMPrediction = async () => {
    setIsPredicting(true);
    setPredictionResult(''); // Clear previous results
    setFetchError(null);
    setAnalysisProgress(t.analysisSteps.preparing);
    setPredictionProgress(0); // Reset progress bar

    const abortController = new AbortController();
    setController(abortController);

    // Simulate progress while waiting for the backend
    const progressInterval = setInterval(() => {
      setPredictionProgress(prevProgress => {
        const newProgress = prevProgress + 1;
        if (newProgress >= 90) { // Stop at 90% to wait for the final response
          return 90;
        }
        return newProgress;
      });
    }, 150); // Increment every 150ms

    // Update analysis steps
    let stepTimeout = setTimeout(() => setAnalysisProgress(t.analysisSteps.training), 2000);
    let stepTimeout2 = setTimeout(() => setAnalysisProgress(t.analysisSteps.predicting), 5000);

    try {
      const response = await fetch(`${BACKEND_URL}/train-and-predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coinId: selectedCoin }),
        signal: abortController.signal
      });

      clearInterval(progressInterval); // Clear the progress interval
      clearTimeout(stepTimeout);
      clearTimeout(stepTimeout2);

      if (response.status === 204) {
        setPredictionResult(t.predictionStopped);
        console.log("Prediction was aborted and backend responded with 204 (No Content).");
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const predictedPrice = parseFloat(result.predictedPrice);

      setPredictionProgress(100); // Set progress to 100% on success
      setPredictionResult(t.predictionSuccess(selectedCoin, formatPrice(predictedPrice)));
      setAnalysisProgress(t.analysisSteps.complete);

      if (apiHistoricalData.length > 0) {
        const lastDate = new Date(apiHistoricalData[apiHistoricalData.length - 1].date.split('/').reverse().join('-'));
        const nextDay = new Date(lastDate);
        nextDay.setDate(lastDate.getDate() + 1);

        const predictionPoint = {
          date: nextDay.toLocaleDateString(language === 'en' ? 'en-US' : 'vi-VN'),
          price: predictedPrice,
          market_cap: null, // We don't have this prediction
          total_volume: null, // We don't have this prediction
          isPrediction: true
        };
        setHistoricalData([...apiHistoricalData, predictionPoint]);
      }

    } catch (error) {
      clearInterval(progressInterval);
      clearTimeout(stepTimeout);
      clearTimeout(stepTimeout2);
      setPredictionProgress(0); // Reset progress on error
      if (error.name === 'AbortError') {
        setPredictionResult(t.predictionStopped);
        console.log('Prediction was aborted by the user. The backend process may still be running.');
      } else {
        console.error("Error getting LSTM prediction from backend:", error);
        setFetchError(t.predictionError(error.message));
        setPredictionResult('Lỗi khi dự đoán giá.');
      }
    } finally {
      setIsPredicting(false);
      setController(null);
    }
  };

  // Function to stop the prediction process
  const stopPrediction = () => {
    if (controller) {
      controller.abort();
    }
  };

  // Filter the coin list based on the search term (case-insensitive)
  const filteredCoins = coinList.filter(coin =>
    coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coin.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get info and icon for the selected coin
  const selectedCoinInfo = coinList.find(coin => coin.id === selectedCoin) || null;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4 font-inter relative">
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-3xl">
        {/* Header section with logo and language selection */}
        <div className="flex flex-col mb-6">
          <div className="flex justify-between items-start w-full">
            {/* Logo component - moved to the left side */}
            <div className="flex-shrink-0">
              <Logo />
            </div>

            {/* Language Selection Dropdown - moved to the right */}
            <div className={`flex flex-col items-end ${isLoading || isPredicting ? 'cursor-not-allowed opacity-50' : ''}`}>
              <div className="flex items-center mb-1">
                <EarthIcon />
                <label className="text-gray-700 text-sm font-semibold mr-2">
                  {t.selectLanguageLabel}
                </label>
              </div>
              <div className={`relative w-50`}>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg pr-9 appearance-none bg-white text-gray-700 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={isLoading || isPredicting}
                >
                  {languageOptions.map(option => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {/* Flag Icon */}
                <img
                  src={languageFlags[language]}
                  alt={`${language} flag`}
                  className="w-5 h-4 rounded-sm shadow-sm absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
                />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center text-gray-800 mt-6">
            {t.appTitle}
          </h1>
        </div>

        <div className="mb-6">
          <label htmlFor="coin-select" className="block text-gray-700 text-sm font-semibold mb-2">
            {t.selectCoinLabel}
          </label>
          <div ref={dropdownRef} className="relative">
            {/* Custom dropdown button */}
            <button
              onClick={() => {
                if (!isLoading && !isPredicting) {
                  setDropdownOpen(!dropdownOpen);
                }
              }}
              className={`flex justify-between items-center w-full p-3 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${isLoading || isPredicting || !selectedCoinInfo ? 'cursor-not-allowed opacity-50' : 'hover:border-blue-500'}`}
              disabled={isLoading || isPredicting || !selectedCoinInfo}
            >
              {selectedCoinInfo ? (
                <span className="flex items-center">
                  <img src={selectedCoinInfo.icon} alt={selectedCoinInfo.name} onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/24x24/d1d5db/4b5563?text=?" }} className="w-6 h-6 mr-3 rounded-full" />
                  <span className="text-gray-900">{selectedCoinInfo.name} ({selectedCoinInfo.symbol.toUpperCase()})</span>
                </span>
              ) : (
                <span className="text-gray-400">{t.fetchingCoinList}</span>
              )}
              <span className="flex items-center text-gray-400">
                {dropdownOpen && (
                  <svg className="h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <svg className={`h-5 w-5 transform transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </button>

            {/* Custom dropdown menu */}
            {dropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                <div className="p-2 sticky top-0 bg-white border-b border-gray-200">
                  <input
                    type="text"
                    placeholder={t.searchCoinPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {filteredCoins.map(coin => (
                  <button
                    key={coin.id}
                    onClick={() => {
                      setSelectedCoin(coin.id);
                      setDropdownOpen(false);
                      setSearchTerm('');
                    }}
                    className={`flex items-center w-full text-left p-3 hover:bg-gray-100 ${selectedCoin === coin.id ? 'bg-blue-50' : ''}`}
                  >
                    <img src={coin.icon} alt={coin.name} onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/24x24/d1d5db/4b5563?text=?" }} className="w-6 h-6 mr-3 rounded-full" />
                    <span className="text-gray-900">{coin.name} ({coin.symbol.toUpperCase()})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {isPredicting ? (
          <button
            onClick={stopPrediction}
            className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75"
            disabled={isLoading}
          >
            {t.stopButtonText}
          </button>
        ) : (
          <button
            onClick={getLSTMPrediction}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
            disabled={isLoading || apiHistoricalData.length === 0}
          >
            {isLoading ? t.loadingButtonText : t.predictButtonText}
          </button>
        )}

        {(isLoading || isPredicting) && (
          <div className="mt-6 text-center text-blue-600 font-medium">
            <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-300 border-t-blue-600 rounded-full"></div>
            <p className="mt-2">{isLoading ? t.loadingMessage : analysisProgress}</p>
          </div>
        )}

        {isPredicting && (
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${predictionProgress}%` }}
            ></div>
          </div>
        )}

        {fetchError && (
          <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p>{fetchError}</p>
          </div>
        )}

        <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h2 className="text-xl font-bold text-blue-800 mb-4">{t.predictionTitle}</h2>
          <p className="text-lg text-blue-700 font-semibold">{predictionResult}</p>
        </div>

        {historicalData.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
              {t.chartTitle}
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={historicalData}
                margin={{ top: 10, right: 0, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis
                  yAxisId="priceY"
                  orientation="left"
                  stroke="#3b82f6"
                  tickFormatter={value => formatPrice(value)}
                  label={{ value: t.priceLabel, angle: -90, position: 'insideLeft' }}
                  fontSize={10}
                />
                <YAxis
                  yAxisId="volumeY"
                  orientation="right"
                  stroke="#82ca9d"
                  tickFormatter={value => formatLargeNumber(value)}
                  label={{ value: `${t.marketCapLabel}/${t.volumeLabel}`, angle: 90, position: 'insideRight' }}
                  fontSize={10}
                />
                <Tooltip
                  formatter={(value, name) => {
                    // UPDATED: Removed the check for predictionLabel as it's no longer a separate line
                    if (name === t.priceLabel) {
                      return [formatPrice(value), t.priceLabel];
                    }
                    if (name === t.marketCapLabel || name === t.volumeLabel) {
                      return [formatLargeNumber(value) + ' USD', name];
                    }
                    return value;
                  }}
                  labelFormatter={(label) => `${t.dateLabel}: ${label}`}
                />
                <Legend />
                <Brush
                  dataKey="date"
                  height={30}
                  stroke="#3b82f6"
                  startIndex={Math.max(0, historicalData.length - 30)}
                  endIndex={historicalData.length - 1}
                >
                  <LineChart>
                    <Line type="monotone" dataKey="price" stroke="#3b82f6" dot={false} />
                  </LineChart>
                </Brush>
                {/* UPDATED: Combined price line and prediction dot */}
                <Line
                  yAxisId="priceY"
                  type="monotone"
                  dataKey="price"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name={t.priceLabel}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (payload.isPrediction) {
                      // Render a special dot for the prediction point
                      return <circle key={payload.date} cx={cx} cy={cy} r={6} fill="#ff7300" stroke="#fff" strokeWidth={2} />;
                    }
                    // For all other points, do not render a dot
                    return null;
                  }}
                  activeDot={(props) => {
                    const { cx, cy, payload, stroke } = props;
                    if (payload.isPrediction) {
                      return <circle r={8} cx={cx} cy={cy} fill="#ff7300" stroke="#fff" strokeWidth={2} />;
                    }
                    // Default active dot for regular points
                    return <circle r={5} cx={cx} cy={cy} fill={stroke} />;
                  }}
                />
                <Line
                  yAxisId="volumeY"
                  type="monotone"
                  dataKey="market_cap"
                  stroke="#ff7300"
                  strokeWidth={2}
                  name={t.marketCapLabel}
                  dot={false}
                />
                <Line
                  yAxisId="volumeY"
                  type="monotone"
                  dataKey="total_volume"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  name={t.volumeLabel}
                  dot={false}
                />
                {/* DELETED: The separate line for the prediction dot has been removed */}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {historicalData.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
              {t.tableTitle}
            </h2>
            <div className="overflow-y-auto max-h-96 rounded-lg shadow-md">
              <table className="min-w-full bg-white text-sm">
                <thead className="bg-gray-200 text-gray-700 sticky top-0">
                  <tr>
                    <th className="py-2 px-4 text-left font-semibold">{t.tableDateHeader}</th>
                    <th className="py-2 px-4 text-left font-semibold">{t.tablePriceHeader}</th>
                    <th className="py-2 px-4 text-left font-semibold">{t.marketCapLabel}</th>
                    <th className="py-2 px-4 text-left font-semibold">{t.volumeLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {historicalData.slice().reverse().map((item, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                      <td className="py-2 px-4 whitespace-nowrap">
                        {item.date} {item.isPrediction && <span className="text-orange-500 font-medium">({t.predictionLabel})</span>}
                      </td>
                      <td className="py-2 px-4 whitespace-nowrap font-medium text-gray-800">
                        {formatPrice(item.price)}
                      </td>
                      <td className="py-2 px-4 whitespace-nowrap font-medium text-gray-800">
                        {item.market_cap ? formatLargeNumber(item.market_cap) : 'N/A'}
                      </td>
                      <td className="py-2 px-4 whitespace-nowrap font-medium text-gray-800">
                        {item.total_volume ? formatLargeNumber(item.total_volume) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showDisclaimer && (
          <div className="mt-8 p-6 bg-yellow-100 rounded-lg border border-yellow-300 text-yellow-800 relative">
            <h3 className="text-lg font-bold mb-2">{t.disclaimerTitle}</h3>
            <button
              onClick={() => setShowDisclaimer(false)}
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              aria-label="Close disclaimer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <p>{t.disclaimerContent}</p>
          </div>
        )}

        {/* Affiliate + Donate area - buttons stacked vertically */}
        <div className="mt-8 p-6 bg-white rounded-lg border border-gray-200 text-center">
          <h2 className="text-lg font-semibold mb-2">{t.donateTitle}</h2>
          <p className="text-sm text-gray-600 mb-3">{t.donateDesc}</p>

          <div className="flex flex-col items-center justify-center gap-3">
            <div className="w-full md:w-2/3">
              <DonatePopup />
            </div>

            <button
              onClick={() => { trackClick('Binance Referral'); window.open('https://www.binance.com/referral/earn-together/refer-in-hotsummer/claim?hl=vi&ref=GRO_20338_VNAZL&utm_source=default', '_blank'); }}
              className="w-full md:w-2/3 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl font-semibold shadow"
            >
              🚀 {t.binanceBtn}
            </button>

            <button
              onClick={() => { trackClick('OKX Referral'); window.open('https://okx.com/join/76936987', '_blank'); }}
              className="w-full md:w-2/3 bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-xl font-semibold shadow"
            >
              🌐 {t.okxBtn}
            </button>

          </div>
        </div>

      </div>

      <footer className="w-full max-w-3xl mt-8 py-4 px-8 bg-gray-800 text-gray-300 text-center rounded-xl shadow-lg">
        <p className="text-sm">{t.footer1} <a href="https://aidream.one" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">AI Dream One</a></p>
        <p className="text-sm"><a href="https://coinvision.aidream.one/" rel="noopener noreferrer" className="text-blue-600 hover:underline">CoinVision.AIdream.one</a></p>
        <p className="text-sm">{t.footer2}</p>
      </footer>

      {showScroll && (
        <button
          onClick={scrollTop}
          className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg transition duration-300 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          aria-label="Scroll to top"
        >
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default App;
